import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["children", "label", "placement", "onChange", "closeOnSelection", "wrapperZIndex", "isMonthSearchable", "isYearSearchable", "menuPortalTarget"];
import { flip, FloatingPortal, offset, useClick, useDismiss, useFloating, useInteractions, shift } from "@floating-ui/react";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
var styles = {
  "datepicker--wrapper": "pbui-2ralt",
  "header-container": "pbui-nkOBN",
  "left-arrow-container": "pbui-9SJ-F",
  "right-arrow-container": "pbui-uXSJo",
  "header-navigate-month": "pbui-eE41A",
  "header-navigate-month-icon": "pbui-AnRA-",
  "header-dropdown-month": "pbui-zDzmd",
  "header-dropdown-year": "pbui-7HtoS"
};
import { getYearsObject } from "./helpers";
import Select from "../Select";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var useOutsideClick = function useOutsideClick(callback) {
  var ref = useRef(null);
  useEffect(function () {
    var handleClickOutside = function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return function () {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [callback]);
  return ref;
};
var selectStyles = {
  control: function control(base, state) {
    return _extends({}, base, {
      display: "flex",
      border: state.isFocused ? "1px solid #84A9FF" : "1px solid rgb(234 236 240)",
      boxShadow: state.isFocused ? "0px 0px 0px 4px #D6E4FF" : "none",
      borderRadius: 4,
      minHeight: "30px"
    });
  },
  placeholder: function placeholder(base) {
    return _extends({}, base, {
      fontSize: 12,
      position: "relative",
      lineHeight: 0
    });
  },
  indicatorSeparator: function indicatorSeparator(base) {
    return _extends({}, base, {
      backgroundColor: "rgb(234 236 240)"
    });
  },
  menu: function menu(base) {
    return _extends({}, base, {
      border: "1px solid rgb(234 236 240)",
      boxShadow: "none",
      Index: 100
    });
  },
  menuPortal: function menuPortal(base) {
    return _extends({}, base, {
      borderColor: "red",
      zIndex: 100
    });
  },
  menuList: function menuList(base) {
    return _extends({}, base, {
      fontSize: 12
    });
  },
  singleValue: function singleValue(base) {
    return _extends({}, base, {
      fontSize: 12
    });
  },
  option: function option(base, state) {
    return _extends({}, base, {
      // eslint-disable-next-line no-nested-ternary
      color: state.isSelected ? "white" : "black",
      backgroundColor: state.isSelected ? "#572BD6" : state.isFocused ? "#CCBFF2" : "transparent",
      ":active": {
        backgroundColor: "#572BD6"
      }
    });
  }
};
var DatePicker = function DatePicker(_ref) {
  var children = _ref.children,
    label = _ref.label,
    placement = _ref.placement,
    _onChange = _ref.onChange,
    _ref$closeOnSelection = _ref.closeOnSelection,
    closeOnSelection = _ref$closeOnSelection === void 0 ? true : _ref$closeOnSelection,
    wrapperZIndex = _ref.wrapperZIndex,
    _ref$isMonthSearchabl = _ref.isMonthSearchable,
    isMonthSearchable = _ref$isMonthSearchabl === void 0 ? true : _ref$isMonthSearchabl,
    _ref$isYearSearchable = _ref.isYearSearchable,
    isYearSearchable = _ref$isYearSearchable === void 0 ? true : _ref$isYearSearchable,
    menuPortalTarget = _ref.menuPortalTarget,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useFloating = useFloating({
      open: open,
      placement: placement || "bottom",
      onOpenChange: function onOpenChange(isOpen) {
        return setOpen(isOpen);
      },
      middleware: [offset(10), flip({
        crossAxis: true,
        fallbackAxisSideDirection: "none",
        padding: 5
      }), shift({
        padding: 5
      })]
    }),
    context = _useFloating.context,
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles;
  var dismiss = useDismiss(context);
  var click = useClick(context, {
    event: "mousedown"
  });
  var _useInteractions = useInteractions([click, dismiss]),
    getReferenceProps = _useInteractions.getReferenceProps,
    getFloatingProps = _useInteractions.getFloatingProps;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/React.isValidElement(children) && /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/React.cloneElement(children, _extends({
        ref: refs.setReference
      }, getReferenceProps()))
    }), open && /*#__PURE__*/_jsx(FloatingPortal, {
      children: /*#__PURE__*/_jsx("div", _extends({
        className: styles["datepicker--wrapper"],
        ref: refs.setFloating
      }, getFloatingProps(), {
        style: _extends({
          zIndex: wrapperZIndex
        }, floatingStyles),
        children: /*#__PURE__*/_jsx(ReactDatePicker, _extends({
          inline: true
        }, props, {
          selected: props.selected,
          timeIntervals: 5,
          renderCustomHeader: function renderCustomHeader(_ref2) {
            var date = _ref2.date,
              changeYear = _ref2.changeYear,
              changeMonth = _ref2.changeMonth,
              decreaseMonth = _ref2.decreaseMonth,
              increaseMonth = _ref2.increaseMonth;
            var getMinYear = props.minDate ? moment(props.minDate).get('year') : moment().subtract(110, 'years').get('year');
            var getMaxYear = props.maxDate ? moment(props.maxDate) : moment();
            var years = getYearsObject(moment().set("year", getMinYear).toDate(), getMaxYear.toDate(), false);
            var months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(function (month, index) {
              return {
                label: month,
                value: index
              };
            });
            return /*#__PURE__*/_jsxs("div", {
              className: styles["header-container"],
              children: [/*#__PURE__*/_jsx("div", {
                className: styles["left-arrow-container"],
                children: /*#__PURE__*/_jsx("span", {
                  "aria-hidden": "true",
                  className: styles["header-navigate-month"],
                  onClick: function onClick() {
                    return decreaseMonth();
                  },
                  children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
                    icon: faAngleLeft,
                    className: styles['header-navigate-month-icon'],
                    size: "sm"
                  })
                })
              }), /*#__PURE__*/_jsx("div", {
                className: styles['header-dropdown-month'],
                children: /*#__PURE__*/_jsx(Select, {
                  instanceId: "month",
                  id: "month",
                  placeholder: "Month",
                  value: months.find(function (month) {
                    return month.label === moment(date).format("MMMM");
                  }),
                  options: months,
                  onChange: function onChange(month) {
                    return changeMonth(month.value);
                  },
                  styles: selectStyles,
                  isSearchable: isMonthSearchable,
                  menuPortalTarget: menuPortalTarget
                })
              }), /*#__PURE__*/_jsx("div", {
                className: styles['header-dropdown-year'],
                children: /*#__PURE__*/_jsx(Select, {
                  instanceId: "year",
                  id: "year",
                  placeholder: "Month",
                  value: {
                    label: moment(date).format("YYYY"),
                    value: Number(moment(date).format("YYYY"))
                  },
                  options: years,
                  onChange: function onChange(month) {
                    return changeYear(month.value);
                  },
                  styles: selectStyles,
                  isSearchable: isYearSearchable,
                  menuPortalTarget: menuPortalTarget
                })
              }), /*#__PURE__*/_jsx("div", {
                className: styles["right-arrow-container"],
                children: /*#__PURE__*/_jsx("span", {
                  "aria-hidden": "true",
                  className: styles["header-navigate-month"],
                  onClick: function onClick() {
                    return increaseMonth();
                  },
                  children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
                    icon: faAngleRight,
                    className: styles['header-navigate-month-icon'],
                    size: "sm"
                  })
                })
              })]
            });
          },
          dateFormat: "Pp",
          onChange: function onChange(date) {
            if (date) {
              _onChange(date);
            }
            if (closeOnSelection) {
              setOpen(false);
            }
          },
          value: props.value
        }))
      }))
    })]
  });
};
export default DatePicker;